import React, { useEffect, useRef, useState } from 'react'

export type MultiSelectOption = {
  value: string
  label: string
}

type MultiSelectDropdownProps = {
  options: MultiSelectOption[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder: string
}

export const MultiSelectDropdown = ({ options, selected, onChange, placeholder }: MultiSelectDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleToggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  const displayText =
    selected.length === 0
      ? placeholder
      : options
          .filter((opt) => selected.includes(opt.value))
          .map((opt) => opt.label)
          .join(', ')

  return (
    <div ref={dropdownRef} className="relative border border-grey rounded-[6px] p-sm w-[200px]">
      <button
        type="button"
        className="text-left w-full flex items-center justify-between"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title={selected.length > 0 ? displayText : undefined}
      >
        <span className={`truncate ${selected.length === 0 ? 'text-grey' : ''}`}>{displayText}</span>
        <span className="ml-2 flex-shrink-0">&#9662;</span>
      </button>
      {isOpen && (
        <div className="absolute left-0 right-0 top-full z-50 bg-white border border-grey shadow-lg">
          {options.map((option) => (
            <label key={option.value} className="flex items-center p-sm cursor-pointer hover:bg-fog">
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={() => handleToggle(option.value)}
                className="mr-2"
              />
              {option.label}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}
